import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../Api';

// Janela de aprovacao da conexao com o I.A Prof.
// Quando o professor pede para conectar o I.A Prof com um e-mail diferente do cadastrado no D.E.V,
// o pedido aparece aqui logo depois do login (vale 5 minutos). Aprovar = o I.A Prof conecta sozinho.
const INTERVALO_BUSCA = 15000; // procura pedido novo a cada 15 s enquanto o professor esta logado

const fmt = (ms) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function PedidoIAProf() {
    const [pedido, setPedido] = useState(null); // { id, expiraEm, contaIaProf, nomeIaProf }
    const [fechado, setFechado] = useState(null); // id do pedido que o professor fechou
    const [resta, setResta] = useState(0);
    const [estado, setEstado] = useState('pedido'); // pedido | enviando | aprovado | erro
    const [erro, setErro] = useState('');
    const diferenca = useRef(0); // relogio do servidor - relogio do aparelho

    const idAtual = useRef(null);
    const estadoAtual = useRef('pedido');
    estadoAtual.current = estado;

    const buscar = useCallback(async () => {
        if (!sessionStorage.getItem('token') || !localStorage.getItem('Id_employee')) return;
        try {
            const r = await api.get('/iaprof/pedido-conexao');
            const p = r?.data?.pedido;
            if (p) {
                diferenca.current = new Date(p.agora).getTime() - Date.now();
                if (idAtual.current !== p.id) {
                    idAtual.current = p.id;
                    setEstado('pedido');
                    setErro('');
                    setPedido(p);
                }
            } else if (estadoAtual.current !== 'aprovado') {
                idAtual.current = null;
                setPedido(null);
            }
        } catch {
            /* sem conexao ou sessao expirada: tenta de novo depois */
        }
    }, []);

    useEffect(() => {
        buscar();
        const t = setInterval(buscar, INTERVALO_BUSCA);
        return () => clearInterval(t);
    }, [buscar]);

    // contador regressivo
    useEffect(() => {
        if (!pedido) return undefined;
        const tick = () => setResta(new Date(pedido.expiraEm).getTime() - (Date.now() + diferenca.current));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [pedido]);

    if (!pedido || fechado === pedido.id) return null;
    const expirou = resta <= 0 && estado !== 'aprovado';

    async function aprovar() {
        setEstado('enviando');
        setErro('');
        try {
            await api.post(`/iaprof/pedido-conexao/${pedido.id}/aprovar`);
            setEstado('aprovado');
        } catch (e) {
            setEstado('erro');
            setErro(e?.response?.data?.msg || 'Não foi possível aprovar. Tente de novo.');
        }
    }

    const cor = resta <= 60000 ? '#dc2626' : '#4f46e5';
    const botao = (fundo, texto) => ({
        width: '100%', border: 'none', borderRadius: 10, padding: '13px 16px', fontSize: 16, fontWeight: 700,
        cursor: 'pointer', background: fundo, color: texto,
    });

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Pedido de conexão do I.A Prof"
            style={{
                position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 16, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            }}
        >
            <div style={{ width: '100%', maxWidth: 440, background: '#fff', color: '#1e293b', borderRadius: 16, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.3)', fontFamily: 'inherit' }}>
                {estado === 'aprovado' ? (
                    <>
                        <div style={{ fontSize: 40, textAlign: 'center' }}>✅</div>
                        <h2 style={{ margin: '8px 0', fontSize: 20, textAlign: 'center' }}>Conexão aprovada!</h2>
                        <p style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.5, textAlign: 'center', color: '#475569' }}>
                            Pode voltar ao <b>I.A Prof</b>: em alguns segundos ele mostra que está conectado ao D.E.V.
                        </p>
                        <button style={botao('#4f46e5', '#fff')} onClick={() => setFechado(pedido.id)}>Fechar</button>
                    </>
                ) : (
                    <>
                        <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>Conectar o I.A Prof ao seu D.E.V?</h2>
                        <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.5, color: '#475569' }}>
                            A conta do I.A Prof{' '}
                            <b style={{ color: '#1e293b' }}>{pedido.nomeIaProf ? `${pedido.nomeIaProf} (${pedido.contaIaProf})` : pedido.contaIaProf}</b>{' '}
                            pediu para usar o seu D.E.V: ver suas turmas e lançar aulas e notas no seu nome.
                        </p>
                        <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.5, background: '#fef3c7', color: '#78350f', borderRadius: 10, padding: '10px 12px' }}>
                            Só aprove se foi <b>você</b> que pediu agora no I.A Prof. Se não foi você, feche esta janela e troque sua senha do D.E.V.
                        </p>
                        <div style={{ textAlign: 'center', margin: '0 0 16px' }}>
                            <div style={{ fontSize: 13, color: '#64748b' }}>{expirou ? 'O pedido expirou' : 'Tempo para aprovar'}</div>
                            <div style={{ fontSize: 34, fontWeight: 800, color: expirou ? '#94a3b8' : cor, fontVariantNumeric: 'tabular-nums' }}>{fmt(resta)}</div>
                        </div>
                        {erro && <p style={{ margin: '0 0 12px', fontSize: 14, background: '#fef2f2', color: '#b91c1c', borderRadius: 10, padding: '10px 12px' }}>{erro}</p>}
                        {expirou ? (
                            <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.5, color: '#475569', textAlign: 'center' }}>
                                O tempo acabou. Peça a conexão de novo no I.A Prof e aprove aqui em até 5 minutos.
                            </p>
                        ) : (
                            <button style={botao('#16a34a', '#fff')} onClick={aprovar} disabled={estado === 'enviando'}>
                                {estado === 'enviando' ? 'Aprovando…' : 'Aprovar conexão com o I.A Prof'}
                            </button>
                        )}
                        <button style={{ ...botao('transparent', '#475569'), marginTop: 8, fontWeight: 600 }} onClick={() => setFechado(pedido.id)}>
                            Fechar
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
